// The browser client for an App's scheduled job (B3): read its state and enable, pause, resume or
// reschedule it. It sends an operation id and, for a new cadence, only a cron and a time zone; the
// server derives the selected publication, its original receipt, the trigger and the consent from
// current authority, and its scheduler validates the cadence (the 15-minute floor). Nothing here
// builds a Job or accepts a success the server did not state. The budget is APP's route.
import {
  parseAppSchedulePresentation,
  type AppSchedulePresentation,
} from './schedule-presentation';
export {
  parseAppSchedulePresentation,
  type AppSchedulePresentation,
} from './schedule-presentation';
export type ScheduleState = {
  state: 'active' | 'paused' | 'not_scheduled';
  nextFireAt: string | null;
  schedule: SchedulePresentation | null;
};
export type SchedulePresentation = AppSchedulePresentation;
export type ScheduleAction = 'enable' | 'pause' | 'resume' | 'reschedule';
/** A cadence the owner chose: a 5-field cron and an IANA time zone. The server decides if it is allowed. */
export type ScheduleChoice = { cron: string; timezone: string };
/** The selected saved version's exact tuple, for a private App's first activation by its owner. */
export type PrivateSetup = { artifactId: string; versionId: string; revisionId: string };
/** The schedule read failed because this App has no activation yet and no publication to select one from. */
export class ScheduleNotActivated extends Error {
  constructor() {
    super('schedule_not_activated');
  }
}
export type ScheduleRefusal = 'not_authorized' | 'invalid' | 'conflict' | 'too_frequent' | 'busy';
export type ScheduleOutcome =
  | { kind: 'ok'; state: ScheduleState }
  | { kind: 'refused'; reason: ScheduleRefusal }
  | { kind: 'unknown' };

const PREFIX = '/api/v1/company/apps/schedules';
const STATES = ['active', 'paused', 'not_scheduled'] as const;

const obj = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

/** Exactly shaped or refused: anything else is not a schedule state. */
export function parseScheduleState(value: unknown): ScheduleState | null {
  const raw = obj(value);
  const state = STATES.find((item) => item === raw?.state);
  if (!raw || !state) return null;
  const next = raw.nextFireAt;
  if (next !== null && typeof next !== 'string') return null;
  const schedule = raw.schedule === null ? null : parseAppSchedulePresentation(raw.schedule);
  if (raw.schedule !== null && !schedule) return null;
  return {
    state,
    nextFireAt: next,
    schedule,
  };
}

export interface AppScheduleApi {
  read(): Promise<ScheduleState>;
  /** One action under one operation id; a thrown/unknown result may still have committed. */
  run(action: Exclude<ScheduleAction, 'reschedule'>, operationId: string): Promise<ScheduleOutcome>;
  /** The owner's first activation of a private App: the exact selected version, one original operation key,
   * and optionally a cadence of the owner's own instead of the declared one. */
  enablePrivate?(
    setup: PrivateSetup,
    operationId: string,
    schedule?: ScheduleChoice,
  ): Promise<ScheduleOutcome>;
  /** The same job on a new cadence, one original operation key. */
  reschedule?(schedule: ScheduleChoice, operationId: string): Promise<ScheduleOutcome>;
}

// Why the server refused, from its status and its stated code only.
async function refusal(
  response: Response,
  parsed: (response: Response) => Promise<unknown>,
): Promise<ScheduleOutcome> {
  const code = response.status >= 400 ? obj(await parsed(response))?.error : null;
  if (code === 'schedule_too_frequent') return { kind: 'refused', reason: 'too_frequent' };
  if (code === 'unsettled_original_run') return { kind: 'refused', reason: 'busy' };
  if (response.status === 403) return { kind: 'refused', reason: 'not_authorized' };
  if (response.status === 409) return { kind: 'refused', reason: 'conflict' };
  if (response.status === 400 || response.status === 422)
    return { kind: 'refused', reason: 'invalid' };
  return { kind: 'unknown' };
}

export function createAppScheduleApi(
  appId: string,
  jobId: string,
  send: typeof fetch,
): AppScheduleApi {
  const url = `${PREFIX}/${encodeURIComponent(appId)}/${encodeURIComponent(jobId)}`;
  const parsed = async (response: Response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };
  return {
    async read() {
      const response = await send(url, { credentials: 'include' });
      const body = await parsed(response);
      const state = response.status === 200 ? parseScheduleState(body) : null;
      if (
        !state &&
        (response.status === 404 || response.status === 409) &&
        obj(body)?.error === 'app_schedule_selection_unavailable'
      )
        throw new ScheduleNotActivated();
      if (!state) throw new Error('schedule_unreadable');
      return state;
    },
    async enablePrivate(setup, operationId, schedule) {
      let response: Response;
      try {
        response = await send(
          `/api/v1/company/apps/${encodeURIComponent(setup.artifactId)}/jobs/${encodeURIComponent(jobId)}/enable`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              versionId: setup.versionId,
              revisionId: setup.revisionId,
              operationId,
              ...(schedule
                ? { schedule: { cron: schedule.cron, timezone: schedule.timezone } }
                : {}),
            }),
          },
        );
      } catch {
        return { kind: 'unknown' };
      }
      if (response.status === 200) {
        // The route states success; the schedule's own read then says what is now active.
        try {
          return { kind: 'ok', state: await createAppScheduleApi(appId, jobId, send).read() };
        } catch {
          return { kind: 'unknown' };
        }
      }
      return refusal(response, parsed);
    },
    async reschedule(schedule, operationId) {
      let response: Response;
      try {
        response = await send(`${url}/reschedule`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            operationId,
            cron: schedule.cron,
            timezone: schedule.timezone,
          }),
        });
      } catch {
        return { kind: 'unknown' };
      }
      if (response.status === 200) {
        const state = parseScheduleState(await parsed(response));
        return state ? { kind: 'ok', state } : { kind: 'unknown' };
      }
      return refusal(response, parsed);
    },
    async run(action, operationId) {
      let response: Response;
      try {
        response = await send(`${url}/${action}`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ operationId }),
        });
      } catch {
        return { kind: 'unknown' };
      }
      if (response.status === 200) {
        const state = parseScheduleState(await parsed(response));
        return state ? { kind: 'ok', state } : { kind: 'unknown' };
      }
      return refusal(response, parsed);
    },
  };
}

/** The next runs of a cadence someone is about to approve, as the server's scheduler computes them from now;
 * null when the server refuses it or cannot be reached. Read only: it changes and stores nothing. */
export async function previewAppSchedule(
  choice: ScheduleChoice,
  send: typeof fetch,
): Promise<string[] | null> {
  const query = new URLSearchParams({ cron: choice.cron, timezone: choice.timezone });
  try {
    const response = await send(`${PREFIX}/preview?${query}`, { credentials: 'include' });
    if (response.status !== 200) return null;
    const schedule = parseAppSchedulePresentation(obj(await response.json())?.schedule);
    return schedule?.nextRuns?.length ? schedule.nextRuns : null;
  } catch {
    return null;
  }
}
